/**
 * Distributes a target total across items using the Largest-Remainder Method.
 * Guarantees that the sum of returned values exactly equals `targetTotal`.
 *
 * Algorithm:
 * 1. Floor each raw amount to `decimals` decimal places
 * 2. Compute remainder = targetTotal − sum(floored)
 * 3. Sort by fractional part descending
 * 4. Distribute one unit (10^−decimals) to top entries until remainder is exhausted
 *
 * @param rawAmounts - Unrounded distribution amounts
 * @param targetTotal - The exact total that must be matched (e.g. staffAllocationAmount)
 * @param decimals - Number of decimal places (default 2, matching numeric(10,2) DB columns)
 * @returns Array of rounded amounts in the same order as input, summing to targetTotal
 *
 * SYNC: An identical implementation exists in
 *       supabase/functions/_shared/serviceCharge.ts — keep both in sync.
 */
export function roundWithLargestRemainder(
  rawAmounts: number[],
  targetTotal: number,
  decimals: number = 2
): number[] {
  if (rawAmounts.length === 0) return [];

  const factor = Math.pow(10, decimals);

  // Step 1: Floor each amount
  const floored = rawAmounts.map(amount => Math.floor(amount * factor) / factor);

  // Step 2: Compute remainder (in integer units to avoid float issues)
  const flooredSumUnits = floored.reduce((sum, val) => sum + Math.round(val * factor), 0);
  const targetUnits = Math.round(targetTotal * factor);
  let remainderUnits = targetUnits - flooredSumUnits;

  // Guard: if flooring somehow exceeded target (float edge case), return floored as-is
  if (remainderUnits <= 0) return floored;

  // Step 3: Build index array sorted by fractional part descending
  const fractionalParts = rawAmounts.map((amount, index) => ({
    index,
    fractional: (amount * factor) - Math.floor(amount * factor),
  }));
  fractionalParts.sort((a, b) => b.fractional - a.fractional);

  // Step 4: Distribute one unit to top entries
  const result = [...floored];
  const oneUnit = 1 / factor;

  for (const { index } of fractionalParts) {
    if (remainderUnits <= 0) break;
    result[index] = Math.round((result[index] + oneUnit) * factor) / factor;
    remainderUnits--;
  }

  return result;
}
